// Layer 3 (Transformation) — the hours-against-dollars butterfly that leads
// the Pareto tab: every cause drawn twice, equipment hours to the left and US$
// of lost mill production to the right. Pure; the UI formats.
//
// Rows come from `valueLostByCategory` so the US$ side reconciles with the
// Value lost tab and the impact band by construction; its `hours` are the same
// unplanned equipment hours the hours Pareto ranks.

import type { DowntimeEvent } from '../maintenance/DowntimeEvents.transform';
import type { CauseCategoryId } from '../maintenance/downtime.seed';
import { valueLostByCategory } from './ValueLost.transform';

export type HoursDollarsRow = {
  causeCategory: CauseCategoryId;
  label: string;
  hours: number;
  eventCount: number;
  dollars: number;
  /** dollars ÷ window total × 100 — unrounded. */
  sharePct: number;
  /** hours ÷ the largest row's hours — 0..1, for the left bar. */
  hoursRatio: number;
  /** dollars ÷ the largest row's dollars — 0..1, for the right bar. */
  dollarsRatio: number;
};

/** Ranked by US$, then by hours (a fully buffered cause still ranks by the
 *  hours it cost the equipment), then by label for a stable order. A cause
 *  with no stoppage in the window has nothing to set against its money and is
 *  left out: absence is not a row. */
export function hoursVersusDollars(rows: readonly DowntimeEvent[]): HoursDollarsRow[] {
  const categories = valueLostByCategory(rows).filter((row) => row.eventCount > 0);
  const maxHours = Math.max(0, ...categories.map((row) => row.hours));
  const maxDollars = Math.max(0, ...categories.map((row) => row.dollars));
  return categories
    .map((row) => ({
      causeCategory: row.causeCategory,
      label: row.label,
      hours: row.hours,
      eventCount: row.eventCount,
      dollars: row.dollars,
      sharePct: row.sharePct,
      hoursRatio: maxHours > 0 ? row.hours / maxHours : 0,
      dollarsRatio: maxDollars > 0 ? row.dollars / maxDollars : 0,
    }))
    .sort((a, b) =>
      b.dollars - a.dollars || b.hours - a.hours || (a.label < b.label ? -1 : a.label > b.label ? 1 : 0));
}
